import { Edit, Link2, Plus, Trash2 } from 'lucide-react';
import { Avatar, Button, Card, Chip, Description } from '@heroui/react';
import type { UserMembership, UserWithMemberships } from '../../../api/users';
import {
  getMembershipRoleChipColor,
  getMembershipRoleLabel,
  getMembershipStatusChipColor,
  getMembershipStatusLabel,
  getUserDisplayName,
  getUserInitials,
} from '../utils/user-labels';

interface UserCardProps {
  user: UserWithMemberships;
  onAssociate: (user: UserWithMemberships) => void;
  onEditMembership: (membership: UserMembership, user: UserWithMemberships) => void;
  onDeleteMembership: (membership: UserMembership, user: UserWithMemberships) => void;
}

export function UserCard({
  user,
  onAssociate,
  onEditMembership,
  onDeleteMembership,
}: UserCardProps) {
  const displayName = getUserDisplayName(user);

  return (
    <Card className="transition-shadow hover:shadow-md">
      <Card.Content className="flex flex-col gap-4 p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-start gap-4">
            <Avatar size="lg" color="accent">
              {user.photoUrl ? <Avatar.Image src={user.photoUrl} alt={displayName} /> : null}
              <Avatar.Fallback>{getUserInitials(user)}</Avatar.Fallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-lg font-semibold text-foreground">{displayName}</p>
              <p className="truncate text-sm text-muted">{user.email}</p>
              {user.documentNumber ? (
                <p className="mt-1 text-sm text-muted">CPF: {user.documentNumber}</p>
              ) : null}
            </div>
          </div>
          <Button size="sm" variant="secondary" onPress={() => onAssociate(user)}>
            <Plus size={16} />
            Associar
          </Button>
        </div>

        {user.memberships.length > 0 ? (
          <div className="flex flex-col gap-2">
            <Description className="font-medium text-foreground">Associações</Description>
            <div className="flex flex-col gap-2">
              {user.memberships.map((membership) => (
                <div
                  key={membership.id}
                  className="flex flex-wrap items-center gap-2 rounded-lg border border-separator bg-surface-secondary px-3 py-2"
                >
                  <Chip size="sm" variant="soft" color={getMembershipRoleChipColor(membership.role)}>
                    {getMembershipRoleLabel(membership.role)}
                  </Chip>
                  <Chip
                    size="sm"
                    variant="soft"
                    color={getMembershipStatusChipColor(membership.status)}
                  >
                    {getMembershipStatusLabel(membership.status)}
                  </Chip>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                    {membership.condominiumName}
                  </span>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      isIconOnly
                      size="sm"
                      variant="ghost"
                      aria-label="Editar associação"
                      onPress={() => onEditMembership(membership, user)}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="ghost"
                      aria-label="Remover associação"
                      onPress={() => onDeleteMembership(membership, user)}
                    >
                      <Trash2 size={16} className="text-danger" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-lg border border-dashed border-separator px-3 py-3 text-sm text-muted">
            <Link2 size={16} className="shrink-0" />
            <span>Sem associações. Use &quot;Associar&quot; para vincular ao condomínio.</span>
          </div>
        )}
      </Card.Content>
    </Card>
  );
}
